/**
 * Detecta nova versão do app candidato (build_id + service worker) e aplica atualização.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { CANDIDATE_PWA_ENABLED } from './candidatePwa';
import {
  getTenantCandidatePwaContracted,
  subscribeTenantCandidatePwaContracted,
} from './tenantPwaContract';

const BUILD_ID_KEY = 'ingressa_app_build_id';

async function fetchServerBuildId() {
  const res = await fetch('/api/pwa/version', {
    cache: 'no-store',
    credentials: 'same-origin',
  });
  if (!res.ok) return null;
  const data = await res.json().catch(() => ({}));
  const id = (data.build_id || '').trim();
  return id && id !== 'unknown' ? id : null;
}

export function useCandidatePwaUpdate() {
  const [updateAvailable, setUpdateAvailable] = useState(false);
  const [busy, setBusy] = useState(false);
  const [tenantContracted, setTenantContracted] = useState(getTenantCandidatePwaContracted());
  const swRegistrationRef = useRef(null);
  const pwaActive = CANDIDATE_PWA_ENABLED && tenantContracted;

  useEffect(() => subscribeTenantCandidatePwaContracted(setTenantContracted), []);

  const markUpdateIfNeeded = useCallback(async () => {
    if (!pwaActive) return;
    try {
      const serverId = await fetchServerBuildId();
      if (!serverId) return;
      const stored = localStorage.getItem(BUILD_ID_KEY);
      if (!stored) {
        localStorage.setItem(BUILD_ID_KEY, serverId);
        return;
      }
      if (stored !== serverId) {
        setUpdateAvailable(true);
      }
    } catch {
      /* rede instável — tenta na próxima abertura */
    }
  }, [pwaActive]);

  useEffect(() => {
    if (!pwaActive || typeof window === 'undefined') return undefined;

    markUpdateIfNeeded();

    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.ready
        .then((registration) => {
          swRegistrationRef.current = registration;
          const onInstalled = (worker) => {
            if (!worker) return;
            worker.addEventListener('statechange', () => {
              if (worker.state === 'installed' && navigator.serviceWorker.controller) {
                setUpdateAvailable(true);
              }
            });
          };
          if (registration.waiting && navigator.serviceWorker.controller) {
            setUpdateAvailable(true);
          }
          registration.addEventListener('updatefound', () => onInstalled(registration.installing));
        })
        .catch(() => {});
    }

    const onVisibility = () => {
      if (document.visibilityState !== 'visible') return;
      markUpdateIfNeeded();
      if ('serviceWorker' in navigator) {
        navigator.serviceWorker.ready.then((r) => r.update()).catch(() => {});
      }
    };
    document.addEventListener('visibilitychange', onVisibility);
    const interval = setInterval(markUpdateIfNeeded, 10 * 60 * 1000);

    return () => {
      document.removeEventListener('visibilitychange', onVisibility);
      clearInterval(interval);
    };
  }, [pwaActive, markUpdateIfNeeded]);

  const applyUpdate = useCallback(async () => {
    if (!pwaActive) return;
    setBusy(true);
    try {
      const registration = swRegistrationRef.current
        || (await navigator.serviceWorker?.ready?.catch(() => null));
      if (registration?.waiting) {
        registration.waiting.postMessage({ type: 'SKIP_WAITING' });
      }
      if ('caches' in window) {
        const keys = await caches.keys();
        await Promise.all(keys.map((key) => caches.delete(key)));
      }
      try {
        const serverId = await fetchServerBuildId();
        if (serverId) localStorage.setItem(BUILD_ID_KEY, serverId);
      } catch {
        /* recarrega mesmo assim */
      }
      const url = new URL(window.location.href);
      url.searchParams.delete('_refresh');
      url.searchParams.set('_refresh', String(Date.now()));
      window.location.replace(url.href);
    } catch {
      window.location.reload();
    }
  }, [pwaActive]);

  return {
    pwaActive,
    updateAvailable,
    applyUpdate,
    busy,
  };
}
